import {
  CaretDownOutlined,
  CaretUpOutlined,
  DownloadOutlined,
  DeleteOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import { Box } from "@strapi/design-system/Box";
import { Flex } from "@strapi/design-system/Flex";
import { IconButton } from "@strapi/design-system/IconButton";
import { Button, Dropdown, Table, Tabs, message, Spin } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  exportData,
  getNominatorsByType,
  getNomineesDeep,
  deleteNominator,
  downloadAttachment,
  downloadNomineeProfilesPdf,
  downloadNomineeProfilesWord,
} from "../util/API";
import Nominees from "./Nominees";
import dayjs from "dayjs";

const PAGE_SIZE_OPTIONS = ["10", "25", "50", "100"];

const Secrectariat = () => {
  const { t } = useTranslation();
  const [nominators, setNominators] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [activeKey, setActiveKey] = useState("1");
  const [nominees, setNominees] = useState([]);
  const [totalNominatorRows, setTotalNominatorRows] = useState(0);
  const [currentNominatorPage, setCurrentNominatorPage] = useState(1);
  const [nominatorPageSize, setNominatorPageSize] = useState(25);
  const [totalOrgRows, setTotalOrgRows] = useState(0);
  const [currentOrgPage, setCurrentOrgPage] = useState(1);
  const [orgPageSize, setOrgPageSize] = useState(25);
  const [totalNomineeRows, setTotalNomineeRows] = useState(0);
  const [currentNomineePage, setCurrentNomineePage] = useState(1);
  const [nomineePageSize, setNomineePageSize] = useState(10);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingNominators, setLoadingNominators] = useState(true);
  const [loadingNominees, setLoadingNominees] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchNominators = useCallback(async () => {
    setLoadingNominators(true);
    try {
      const response = await getNominatorsByType(
        false,
        currentNominatorPage,
        nominatorPageSize
      );
      const rows = response.data?.data ?? [];
      const metaTotal = response.data?.meta?.pagination?.total ?? 0;
      rows.forEach(row => {
        row.attributes.nominatees.count = row.attributes.nominatees.data.length;
      });
      setNominators(rows);
      setTotalNominatorRows(metaTotal);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingNominators(false);
    }
  }, [currentNominatorPage, nominatorPageSize]);

  const fetchOrganizations = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const response = await getNominatorsByType(
        true,
        currentOrgPage,
        orgPageSize
      );
      const rows = response.data?.data ?? [];
      const metaTotal = response.data?.meta?.pagination?.total ?? 0;
      rows.forEach(row => {
        row.attributes.nominatees.count = row.attributes.nominatees.data.length;
      });
      setOrganizations(rows);
      setTotalOrgRows(metaTotal);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOrgs(false);
    }
  }, [currentOrgPage, orgPageSize]);

  const fetchNomineeSelfElected = useCallback(async () => {
    setLoadingNominees(true);
    try {
      const response = await getNomineesDeep(
        currentNomineePage,
        nomineePageSize
      );
      if (response?.status === 200) {
        const data = response.data?.data ?? [];
        setNominees(data);
        const metaTotal = response.data?.meta?.pagination?.total ?? 0;
        setTotalNomineeRows(metaTotal);
        return data;
      }
      return [];
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setLoadingNominees(false);
    }
  }, [currentNomineePage, nomineePageSize]);

  const nominatorColumns = [
    {
      title: t("str_ordinal_number"),
      key: "index",
      render: (_, __, index) =>
        (currentNominatorPage - 1) * nominatorPageSize + index + 1,
    },
    {
      title: t("str_nominator_name"),
      dataIndex: ["attributes", "name"],
    },
    {
      title: t("str_dob"),
      dataIndex: ["attributes", "dob"],
    },
    {
      title: t("str_phone_number"),
      dataIndex: ["attributes", "phone"],
    },
    {
      title: "Email",
      dataIndex: ["attributes", "email"],
    },
    {
      title: "Nominatees",
      dataIndex: ["attributes", "nominatees", "count"],
      align: "center",
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Flex>
          <Box paddingLeft={1}>
            <IconButton
              onClick={async () => {
                await deleteNominator(record.id);
                fetchNominators();
              }}
              label="Delete"
              noBorder
              icon={<DeleteOutlined />}
            />
          </Box>
        </Flex>
      ),
    },
  ];

  const orgColumns = [
    {
      title: t("str_ordinal_number"),
      key: "index",
      render: (_, __, index) =>
        (currentOrgPage - 1) * orgPageSize + index + 1,
    },
    {
      title: t("str_nominator_name"),
      dataIndex: ["attributes", "name"],
    },
    {
      title: t("str_dob"),
      dataIndex: ["attributes", "dob"],
    },
    {
      title: t("str_phone_number"),
      dataIndex: ["attributes", "phone"],
    },
    {
      title: "Email",
      dataIndex: ["attributes", "email"],
    },
    {
      title: "Nominatees",
      dataIndex: ["attributes", "nominatees", "count"],
      align: "center",
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Flex>
          <Box paddingLeft={1}>
            <IconButton
              onClick={async () => {
                await deleteNominator(record.id);
                fetchOrganizations();
              }}
              label="Delete"
              noBorder
              icon={<DeleteOutlined />}
            />
          </Box>
        </Flex>
      ),
    },
  ];

  const tabItems = [
    {
      key: "1",
      label: t("str_org_candidate"),
      children: (
        <Spin key={`org-${organizations.length}`} spinning={loadingOrgs}>
          <Table
            rowKey="id"
            columns={orgColumns}
            expandable={{
              expandedRowRender: (record) => (
                <Nominees
                  nomineesData={record?.attributes?.nominatees?.data}
                  isSecretariatView={true}
                />
              ),
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <CaretUpOutlined onClick={(e) => onExpand(record, e)} />
                ) : (
                  <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
                ),
            }}
            dataSource={organizations}
            scroll={{ x: "1000px", y: 480 }}
            pagination={{
              current: currentOrgPage,
              pageSize: orgPageSize,
              total: totalOrgRows,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: (page, size) => {
                setCurrentOrgPage(page);
                setOrgPageSize(size);
              },
            }}
          />
        </Spin>
      ),
    },
    {
      key: "2",
      label: t("str_nominator_candidate"),
      children: (
        <Spin key={`nominators-${nominators.length}`} spinning={loadingNominators}>
          <Table
            rowKey="id"
            columns={nominatorColumns}
            expandable={{
              expandedRowRender: (record) => (
                <Nominees
                  nomineesData={record?.attributes?.nominatees?.data}
                  isSecretariatView={true}
                />
              ),
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <CaretUpOutlined onClick={(e) => onExpand(record, e)} />
                ) : (
                  <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
                ),
            }}
            dataSource={nominators}
            scroll={{ x: "1000px", y: 480 }}
            pagination={{
              current: currentNominatorPage,
              pageSize: nominatorPageSize,
              total: totalNominatorRows,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: (page, size) => {
                setCurrentNominatorPage(page);
                setNominatorPageSize(size);
              },
            }}
          />
        </Spin>
      ),
    },
    {
      key: "3",
      label: t("str_self_candidate"),
      children: (
        <Spin key={`self-${nominees.length}`} spinning={loadingNominees}>
          <Nominees
            nomineesData={nominees}
            isSecretariatView={true}
            getNomineeSelfElected={fetchNomineeSelfElected}
            secretariatPagination={{
              current: currentNomineePage,
              pageSize: nomineePageSize,
              total: totalNomineeRows,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: (page, size) => {
                setCurrentNomineePage(page);
                setNomineePageSize(size);
              },
            }}
          />
        </Spin>
      ),
    },
  ];

  useEffect(() => {
    fetchNomineeSelfElected();
  }, [fetchNomineeSelfElected]);

  useEffect(() => {
    fetchNominators();
  }, [fetchNominators]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await exportData();
      if (response.data?.status && response.data?.url) {
        const fileName = "nomination_report_" + dayjs().format("x") + ".xlsx";
        const link = document.createElement("a");
        link.href = response.data.url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadAttachment = async () => {
    setDownloading(true);
    try {
      const response = await downloadAttachment();
      if (response.data?.status) {
        messageApi.open({
          type: "success",
          content: t("str_download_attachment_success"),
        });
      }
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Retry",
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPdf = async (locale = "vi") => {
    setDownloadingPdf(true);
    try {
      const response = await downloadNomineeProfilesPdf(locale);
      if (response.data?.url) {
        const fileName =
          response.data.fileName ||
          `nomination_profiles_${dayjs().format("x")}.zip`;
        const link = document.createElement("a");
        link.href = response.data.url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        if (response.data.errors?.length) {
          messageApi.open({
            type: "warning",
            content: `Đã tải ${response.data.generated} PDF. ${response.data.errors.length} hồ sơ lỗi.`,
            duration: 6,
          });
        } else {
          messageApi.open({
            type: "success",
            content: `Đã tải ${response.data.generated} hồ sơ PDF.`,
          });
        }
      }
    } catch (error) {
      console.error(error);
      messageApi.open({
        type: "error",
        content: error.response?.data?.message || "Không tải được PDF.",
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadWord = async (locale = "vi") => {
    setDownloadingWord(true);
    try {
      const response = await downloadNomineeProfilesWord(locale);
      if (response.data?.url) {
        const fileName =
          response.data.fileName ||
          `nomination_profiles_word_${dayjs().format("x")}.zip`;
        const link = document.createElement("a");
        link.href = response.data.url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        if (response.data.errors?.length) {
          messageApi.open({
            type: "warning",
            content: `Đã tải ${response.data.generated} Word. ${response.data.errors.length} hồ sơ lỗi.`,
            duration: 6,
          });
        } else {
          messageApi.open({
            type: "success",
            content: `Đã tải ${response.data.generated} hồ sơ Word.`,
          });
        }
      }
    } catch (error) {
      console.error(error);
      messageApi.open({
        type: "error",
        content: error.response?.data?.message || "Không tải được Word.",
      });
    } finally {
      setDownloadingWord(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Flex justifyContent="end">
        <Box paddingBottom={2}>
          <Button
            icon={downloading ? <LoadingOutlined /> : <DownloadOutlined />}
            loading={downloading}
            onClick={handleDownloadAttachment}
          >
            Download Attachments
          </Button>
        </Box>
        <Box paddingBottom={2} paddingLeft={2}>
          <Dropdown
            disabled={downloadingPdf}
            menu={{
              items: [
                { key: "vi", label: "Tiếng Việt" },
                { key: "en", label: "English" },
              ],
              onClick: ({ key }) => handleDownloadPdf(key),
            }}
          >
            <Button
              icon={downloadingPdf ? <LoadingOutlined /> : <DownloadOutlined />}
              loading={downloadingPdf}
            >
              Download PDF <CaretDownOutlined />
            </Button>
          </Dropdown>
        </Box>
        <Box paddingBottom={2} paddingLeft={2}>
          <Dropdown
            disabled={downloadingWord}
            menu={{
              items: [
                { key: "vi", label: "Tiếng Việt" },
                { key: "en", label: "English" },
              ],
              onClick: ({ key }) => handleDownloadWord(key),
            }}
          >
            <Button
              icon={downloadingWord ? <LoadingOutlined /> : <DownloadOutlined />}
              loading={downloadingWord}
            >
              Download Word <CaretDownOutlined />
            </Button>
          </Dropdown>
        </Box>
        <Box paddingBottom={2} paddingLeft={2}>
          <Button
            icon={exporting ? <LoadingOutlined /> : <DownloadOutlined />}
            loading={exporting}
            onClick={handleExportData}
          >
            Export data
          </Button>
        </Box>
      </Flex>
      <div>
        <Tabs
          activeKey={activeKey}
          items={tabItems}
          tabPosition="top"
          animated={false}
          onChange={setActiveKey}
        />
      </div>
    </>
  );
};

export default Secrectariat;
